import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs'

import { metaDataContent } from '../../../../../locale/meta-data/meta-data.content.en'
import { PostLayout } from '../../../../shared/enums/post-layout.enum'
import { ResourceSlug } from '../../../../shared/enums/wordpress/wp-resource-slug.enum'
import { Author } from '../../../../shared/interfaces/author.interface'
import { BreadcrumbLink } from '../../../../shared/interfaces/breadcrumb-link.interface'
import { Paginator } from '../../../../shared/interfaces/paginator.interface'
import { Document } from '../../../../shared/interfaces/resources/document.interface'
import { Post } from '../../../../shared/interfaces/resources/post.interface'
import { TaxonomyTerm } from '../../../../shared/interfaces/taxonomy-term.interface'
import { AuthorService } from '../../../../shared/services/author.service'
import { BlogResourceService } from '../../../../shared/services/blog-resource.service'
import { BreadcrumbService } from '../../../../shared/services/breadcrumb.service'
import { DocumentService } from '../../../../shared/services/document.service'
import { FlashMessageService } from '../../../../shared/services/flash-message.service'
import { MetaService } from '../../../../shared/services/meta.service'
import { PostService } from '../../../../shared/services/post.service'

@Component({
  selector: 'app-blog-index',
  templateUrl: './blog-index.component.html',
  styleUrls: ['./blog-index.component.scss']
})
export class BlogIndexComponent implements OnInit {
  @ViewChild('searchInput', { static: true })
  searchEl: ElementRef

  mainFeaturedPost: Post
  otherFeaturedPosts: Post[]
  postPaginator: Paginator<Post>
  documents: Document[]
  authors: Author[]

  stakeholderTypes: TaxonomyTerm[]
  aiPrinciples: TaxonomyTerm[]

  loadingFeaturedPosts: boolean
  loadingPosts: boolean
  loadingDocuments: boolean
  loadingAuthors: boolean

  breadcrumbLinks: BreadcrumbLink[]
  PostLayout = PostLayout

  postForm: FormGroup

  constructor(
    private postService: PostService,
    private documentService: DocumentService,
    private authorService: AuthorService,
    private blogResourceService: BlogResourceService,
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private flashMessageService: FlashMessageService,
    breadcrumbService: BreadcrumbService,
    metaService: MetaService
  ) {
    metaService.setTags(metaDataContent.blogIndex)
    this.breadcrumbLinks = breadcrumbService.getBreadCrumbLinks('blogIndex')
  }

  ngOnInit() {
    this.postForm = this.formBuilder.group({
      stakeholderTypeId: '',
      aiPrincipleId: '',
      authorIds: [],
      terms: ''
    })

    fromEvent(this.searchEl.nativeElement, 'keyup')
      .pipe(
        map((event: any) => event.target.value),
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe((terms: string) => {
        this.filterPosts('terms', terms)
      })

    // Get featured posts.
    this.loadingFeaturedPosts = true
    this.postService
      .listUnpaginated({ featured: true, limitTo: 4 })
      .then((postRes: Post[]) => {
        this.loadingFeaturedPosts = false

        if (postRes.length) {
          this.mainFeaturedPost = postRes[0]
          this.otherFeaturedPosts = postRes.slice(1)
        }
      })
      .catch((err) => {
        this.loadingFeaturedPosts = false
        this.flashMessageService.error(err.message)
      })

    this.activatedRoute.queryParams.subscribe((queryParams: Params) => {
      this.postPaginator = null
      this.loadingPosts = true

      if (queryParams.stakeholderTypeId) {
        this.postForm
          .get('stakeholderTypeId')
          .setValue(queryParams.stakeholderTypeId)
      }

      if (queryParams.aiPrincipleId) {
        this.postForm.get('aiPrincipleId').setValue(queryParams.aiPrincipleId)
      }

      if (queryParams.authorIds) {
        this.postForm.get('authorIds').setValue(queryParams.authorIds)
      }

      if (queryParams.terms) {
        this.postForm.get('terms').setValue(queryParams.terms)
        this.searchEl.nativeElement.value = queryParams.terms
      }

      this.postService
        .listPaginated({
          page: queryParams.page || 1,
          stakeholderTypeId: queryParams.stakeholderTypeId,
          AIPrincipleId: queryParams.aiPrincipleId,
          authorIds: queryParams.authorIds,
          terms: queryParams.terms
        })
        .then((postRes: Paginator<Post>) => {
          this.loadingPosts = false
          this.postPaginator = postRes
        })

      this.loadingDocuments = true
      this.documentService
        .list(
          queryParams.stakeholderTypeId
            ? {
                stakeholderTypeId: queryParams.stakeholderTypeId
              }
            : { featured: true }
        )
        .then((documentRes: Document[]) => {
          this.loadingDocuments = false
          this.documents = documentRes
        })
    })

    // Get authors.
    this.loadingAuthors = true
    ;(
      this.authorService.list({
        featured: true,
        withoutPagination: true
      }) as Promise<Author[]>
    ).then((authorRes: Author[]) => {
      this.loadingAuthors = false
      this.authors = authorRes
    })

    // Get taxonomy terms.
    ;(
      this.blogResourceService.list(ResourceSlug.Principles, {
        withoutPagination: true
      }) as Promise<TaxonomyTerm[]>
    ).then((aiPrincipleRes: TaxonomyTerm[]) => {
      this.aiPrinciples = aiPrincipleRes
    })
    ;(
      this.blogResourceService.list(ResourceSlug.StakeholderTypes, {
        withoutPagination: true
      }) as Promise<TaxonomyTerm[]>
    ).then((stakeholderTypeRes: TaxonomyTerm[]) => {
      this.stakeholderTypes = stakeholderTypeRes
    })
  }

  filterPosts(propName: string, value: string) {
    const queryParams: Params = { page: 1 }

    queryParams[propName] = value

    this.router.navigate(['/wonk'], {
      queryParams,
      queryParamsHandling: 'merge'
    })
  }

  goToPage(pageNumber: number) {
    const queryParams: Params = {
      page: pageNumber.toString()
    }
    this.router.navigate(['/wonk'], {
      queryParams,
      queryParamsHandling: 'merge'
    })
  }
}
